import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

import { 
  Clock, 
  BookOpen, 
  Home, 
  User, 
  Settings, 
  BarChart, 
  BookCheck,
  HelpCircle,
  Info,
  LightbulbIcon,
  CheckCircle2,
  Menu as MenuIcon,
  UserPlus,
  FileCog,
  FileStack,
  ChevronDown,
  LogOut,
  UserCog,
  SquareStack
} from 'lucide-react';

export default function HeaderMenu() {
  const [userMenuOpened, setUserMenuOpened] = useState(false);
  const { session, user, signOut, meta, hasPermissions } = useAuth();
  const [drawerOpened, setDrawerOpened] = useState(false);
  
  // Refs for the menu and button
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const drawerButtonRef = useRef<HTMLButtonElement>(null);
  
  const toggleDrawer = () => setDrawerOpened(!drawerOpened);
  const closeDrawer = () => setDrawerOpened(false);

  // Handle clicks outside of user menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if user menu is open and click is outside of menu and button
      if (
        userMenuOpened && 
        userMenuRef.current && 
        userMenuButtonRef.current && 
        !userMenuRef.current.contains(event.target as Node) && 
        !userMenuButtonRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpened(false);
        // Remove focus from the button
        if (document.activeElement === userMenuButtonRef.current) {
          userMenuButtonRef.current.blur();
        }
      }
      
      // Check if mobile drawer is open and click is outside of drawer
      if (
        drawerOpened && 
        drawerRef.current && 
        drawerButtonRef.current && 
        !drawerRef.current.contains(event.target as Node) && 
        !drawerButtonRef.current.contains(event.target as Node)
      ) {
        closeDrawer();
        // Remove focus from the button
        if (document.activeElement === drawerButtonRef.current) {
          drawerButtonRef.current.blur();
        }
      }
    };

    // Add event listener when menu is opened
    if (userMenuOpened || drawerOpened) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpened, drawerOpened]);

  return (
    <div>
      <header className="w-full bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100 shadow-sm py-2">
        <div className="flex h-16 items-center px-4 sm:px-6 mx-auto max-w-7xl justify-between">
          <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-800 bg-clip-text text-transparent">
            LSAT Gains
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden sm:flex items-center space-x-6">
            {user && !user.is_anonymous && (
              <>
                <Link to="/dashboard" className="px-3 py-2 text-gray-700 hover:text-indigo-600 transition-colors">
                  Tests
                </Link>
                <Link to="/cards" className="px-3 py-2 text-gray-700 hover:text-indigo-600 transition-colors">
                  Cards
                </Link>
                <Link to="/order" className="px-3 py-2 text-gray-700 hover:text-indigo-600 transition-colors">
                  Purchase Tests
                </Link>
                <Link to="/community" className="px-3 py-2 text-gray-700 hover:text-indigo-600 transition-colors">
                  Community
                </Link>
                <Link to="/performance" className="px-3 py-2 text-gray-700 hover:text-indigo-600 transition-colors ">
                  Performance
                </Link>

                {/* User Menu */}
                <div className="relative">
                  <button
                    ref={userMenuButtonRef}
                    onClick={() => setUserMenuOpened(!userMenuOpened)}
                    className={`flex items-center text-sm font-medium rounded-full focus:outline-none focus:ring-2 focus:ring-offset-4 focus:ring-indigo-500 ${
                      userMenuOpened ? 'text-indigo-600' : 'text-gray-700'
                    }`}
                  >
                    <span className="truncate">
                      {meta?.fullName || session?.user?.email}
                    </span>
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </button>

                  {userMenuOpened && (
                    <div 
                      ref={userMenuRef}
                      className="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                    >
                      {hasPermissions(['Administrator']) && (
                        <>
                          <div className="px-4 py-2 text-xs text-gray-500">Administrator</div>
                          <Link
                            to="/admin/passages"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setUserMenuOpened(false)}
                          >
                            <FileStack className="mr-3 h-4 w-4 text-gray-400" />
                            Passages & Questions
                          </Link>
                          <Link
                            to="/admin/tests"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setUserMenuOpened(false)}
                          >
                            <FileCog className="mr-3 h-4 w-4 text-gray-400" />
                            Test Configurations
                          </Link>
                          <Link
                            to="/admin/cards"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setUserMenuOpened(false)}
                          >
                            <SquareStack className="mr-3 h-4 w-4 text-gray-400" />
                            Cards
                          </Link>
                          <div className="border-t border-gray-200 my-1"></div>
                        </>
                      )}
                      <div className="px-4 py-2 text-xs text-gray-500">Personal</div>
                      <Link
                        to="/account-settings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setUserMenuOpened(false)}
                      >
                        <UserCog className="mr-3 h-4 w-4 text-gray-400" />
                        Settings
                      </Link>
                      <div className="border-t border-gray-200 my-1"></div>
                      <div className="px-4 py-2 text-xs text-gray-500">Account</div>
                      <button
                        onClick={() => {
                          setUserMenuOpened(false);
                          signOut();
                        }}
                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <LogOut className="mr-3 h-4 w-4 text-gray-400" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
            {/* {!user && (
              <Link to="/tools/lsat-calculator/" className="px-3 py-2 text-gray-700 hover:text-indigo-600 transition-colors">
                Score Calculator
              </Link>
            )} */}
          </div>

          {/* Login/Sign Up Buttons (Desktop) */}
          <div className="hidden sm:flex items-center space-x-4">
            {(!user || user.is_anonymous) && (
              <>
                <Link 
                  to="/login" 
                  className="px-5 py-2.5 text-gray-700 font-medium border border-gray-300 rounded-lg shadow-sm hover:text-indigo-600 hover:border-indigo-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50"
                >
                  Log in
                </Link>
                <Link 
                  to="/register" 
                  className="px-5 py-2.5 font-medium rounded-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50"
                >
                  Sign up
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            ref={drawerButtonRef}
            className="sm:hidden flex items-center" 
            onClick={toggleDrawer}
            aria-label="Toggle mobile menu"
          >
            <MenuIcon className="h-6 w-6 text-gray-700" />
          </button>
        </div>
      </header>

      {/* Mobile Drawer */}
      {drawerOpened && (
        <div className="fixed inset-0 z-50 flex sm:hidden">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity" 
            onClick={closeDrawer}
          ></div>
          
          {/* Drawer Panel */}
          <div 
            ref={drawerRef}
            className="relative flex-1 flex flex-col max-w-full w-full bg-white shadow-xl"
          >
            <div className="flex items-center justify-between px-4 py-6">
              <div className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-800 bg-clip-text text-transparent">
                LSAT Gains
              </div>
              <button 
                onClick={closeDrawer} 
                className="rounded-md text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close menu</span>
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="border-t border-gray-200 my-2"></div>
            
            <div className="flex-1 overflow-y-auto">
              {user && !user.is_anonymous && (
                <>
                  {hasPermissions(['Administrator']) && (
                    <>
                      <div className="px-4 py-2 text-xs text-gray-500">Administrator</div>
                      <Link
                        to="/admin/passages"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={closeDrawer}
                      >
                        <FileStack className="mr-3 h-4 w-4 text-gray-400" />
                        Passages & Questions
                      </Link>
                      <Link
                        to="/admin/tests"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={closeDrawer}
                      >
                        <FileCog className="mr-3 h-4 w-4 text-gray-400" />
                        Test Configurations
                      </Link>
                      <Link
                        to="/admin/cards"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={closeDrawer}
                      >
                        <SquareStack className="mr-3 h-4 w-4 text-gray-400" />
                        Cards
                      </Link>
                      <div className="border-t border-gray-200 my-1"></div>
                    </>
                  )}
                  <div className="flex flex-col px-4 py-2 space-y-4">
                    <Link 
                      to="/tests" 
                      className="px-3 py-2 text-gray-700 hover:text-indigo-600 transition-colors"
                      onClick={closeDrawer}
                    >
                      Tests
                    </Link>
                    <Link 
                      to="/order" 
                      className="px-3 py-2 text-gray-700 hover:text-indigo-600 transition-colors"
                      onClick={closeDrawer}
                    >
                      Purchase Tests
                    </Link>
                    <Link
                      to="/community"
                      className="px-3 py-2 text-gray-700 hover:text-indigo-600 transition-colors"
                      onClick={closeDrawer}
                    >
                      Community
                    </Link>
                    <Link
                      to="/performance"
                      className="px-3 py-2 text-gray-700 hover:text-indigo-600 transition-colors"
                      onClick={closeDrawer}
                    >
                      Performance
                    </Link>
                    <Link 
                      to="/account-settings" 
                      className="px-3 py-2 text-gray-700 hover:text-indigo-600 transition-colors"
                      onClick={closeDrawer}
                    >
                      Account Settings
                    </Link>
                  </div>
                  <div className="border-t border-gray-200 my-2"></div>
                </>
              )}
              
              <div className="px-4 py-4 flex justify-center">
                {user && !user.is_anonymous ? (
                  <button
                    onClick={() => {
                      signOut();
                      closeDrawer();
                    }}
                    className="w-full px-4 py-2 text-red-600 font-medium border border-red-300 rounded-md hover:bg-red-50 transition-colors"
                  >
                    Sign Out
                  </button>
                ) : (
                  <div className="w-full flex flex-col space-y-3">
                    <Link 
                      to="/login" 
                      className="w-full px-5 py-2.5 text-center text-gray-700 font-medium border border-gray-300 rounded-lg shadow-sm hover:text-indigo-600 hover:border-indigo-300 transition-all duration-200"
                      onClick={closeDrawer}
                    >
                      Log in
                    </Link>
                    <Link 
                      to="/register" 
                      className="w-full px-5 py-2.5 text-center font-medium rounded-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-md transform hover:-translate-y-0.5 transition-all duration-200"
                      onClick={closeDrawer}
                    >
                      Sign up
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
